import type {
	ItemResponse,
	ResponseConfig,
	VideoPlayerConfig,
} from "@memorang/types";

// TEMP: formative items should allow all playback controls (replay included).
// Flip to false to re-restrict formative playback once revisited.
const ALLOW_ALL_CONTROLS_FOR_FORMATIVE = true;

export const getResponseTimeLimit = (
	response?: ItemResponse,
): number | undefined => response?.config?.timeLimit;

// Numeric prep delays item timers; the object shape only orchestrates
// audio/mic sequencing (usePrepSequence) and never shifts timers.
export const getResponsePrepTime = (
	response?: ItemResponse,
): number | undefined => {
	const prepTime = response?.config?.prepTime;
	return typeof prepTime === "number" ? prepTime : undefined;
};

const responseHasTimeLimit = (response?: ItemResponse): boolean =>
	getResponseTimeLimit(response) != null;

export const getResponseMicDelaySeconds = (response?: ItemResponse): number => {
	const prepTime = response?.config?.prepTime;
	return typeof prepTime === "object"
		? (prepTime.microphoneDelaySeconds ?? 0)
		: 0;
};

export const getSummativeResponseConfig = (
	response?: ItemResponse,
	isSummativeTest?: boolean,
): ResponseConfig | undefined =>
	isSummativeTest ? response?.config : undefined;

export const getAllowAudioReplay = (
	config: ResponseConfig | undefined,
	isSummativeTest?: boolean,
): boolean | undefined =>
	ALLOW_ALL_CONTROLS_FOR_FORMATIVE && !isSummativeTest
		? true
		: config?.allowAudioReplay;

export const getAllowVideoReplay = (
	config: ResponseConfig | undefined,
	isSummativeTest?: boolean,
): boolean | undefined =>
	ALLOW_ALL_CONTROLS_FOR_FORMATIVE && !isSummativeTest
		? true
		: config?.allowVideoReplay;

export const getVideoPlayerConfig = (
	config: ResponseConfig | undefined,
	isSummativeTest?: boolean,
): VideoPlayerConfig => {
	if (ALLOW_ALL_CONTROLS_FOR_FORMATIVE && !isSummativeTest) {
		return {
			allowVideoPlayPause: true,
			showVideoProgress: true,
			allowVideoCaptions: true,
			allowVideoFullscreen: true,
		};
	}
	return {
		allowVideoPlayPause: config?.videoPlayerConfig?.allowVideoPlayPause ?? true,
		showVideoProgress: config?.videoPlayerConfig?.showVideoProgress ?? true,
		allowVideoCaptions: config?.videoPlayerConfig?.allowVideoCaptions ?? false,
		allowVideoFullscreen:
			config?.videoPlayerConfig?.allowVideoFullscreen ?? true,
	};
};

export const getVideoProgressSeekable = (isSummativeTest?: boolean): boolean =>
	ALLOW_ALL_CONTROLS_FOR_FORMATIVE && !isSummativeTest;

export const isItemTimerStartAfterAudio = (config?: ResponseConfig): boolean =>
	config?.itemTimerStartTrigger === "AFTER_AUDIO_COMPLETION";

export const isItemTimerStartAfterVideo = (config?: ResponseConfig): boolean =>
	config?.itemTimerStartTrigger === "AFTER_VIDEO_COMPLETION";

// Recorder flows announce prep as a number (mic countdown) or an explicit
// microphone delay; {audioDelaySeconds} alone only orchestrates audio and has
// no recorder driving the timer.
const responseHasRecorderPrep = (response?: ItemResponse): boolean => {
	const prepTime = response?.config?.prepTime;
	if (typeof prepTime === "number") {
		return true;
	}
	return (
		typeof prepTime === "object" && prepTime.microphoneDelaySeconds != null
	);
};

const responseIsRecorderTimed = (response?: ItemResponse): boolean =>
	responseHasRecorderPrep(response) && responseHasTimeLimit(response);

// Writing configs carry only a timeLimit — no field identifies them, so a
// timeLimit without any audio/recorder signal is assumed to be a writing item
// whose editor UI runs the countdown.
const responseAssumedWritingTimed = (response?: ItemResponse): boolean =>
	responseHasTimeLimit(response) &&
	!isItemTimerStartAfterAudio(response?.config);

// Self-timed items complete themselves (markResponseComplete); external
// clocks — auto-skip and resume subtraction — must back off for them.
export const responseIsSelfTimed = (response?: ItemResponse): boolean =>
	responseIsRecorderTimed(response) || responseAssumedWritingTimed(response);

export const getNextGatedUntilResponseComplete = (
	response?: ItemResponse,
	isSummativeTest?: boolean,
): boolean =>
	Boolean(isSummativeTest) &&
	responseIsRecorderTimed(response) &&
	!response?.config?.allowNextBeforeTimeout;

export const getAllowPrevNavigation = (config?: ResponseConfig): boolean =>
	config?.allowPrevNavigation ?? true;

export const getNextGatedUntilAudioAnswered = ({
	config,
	hasAudio,
	hasSelectedAnswer,
}: {
	config?: ResponseConfig;
	hasAudio: boolean;
	hasSelectedAnswer: boolean;
}): boolean =>
	Boolean(config) &&
	!config?.allowAnswerBeforeAudioCompletion &&
	!config?.allowNextBeforeTimeout &&
	hasAudio &&
	!hasSelectedAnswer;

// Only item-timed responses (overrideTestTimerWithItemTimer) react to expiry:
// autoSkipOnTimeout picks between silently advancing (true) and showing the
// time-expired dialog.
export const getResponseTimesOut = (
	response?: ItemResponse,
	isSummativeTest?: boolean,
): boolean =>
	Boolean(isSummativeTest) &&
	Boolean(response?.config?.overrideTestTimerWithItemTimer) &&
	responseHasTimeLimit(response);
